import moment from 'moment';
import { Toaster } from '../common-services/Toast/ToastService';
import { ProfileType } from '../enums/ProfileType';
import { Session } from '../models/Session';
import { ProfileHelper } from './ProfileHelper';

const SESSION_EXPIRES_ALERT_MINUTES = Number(process.env.REACT_APP_SESSION_EXPIRES_ALERT_MINUTES);

const getStateExpiration = (session?: Session | null) => {
  const sessionExpiredAfter = SESSION_EXPIRES_ALERT_MINUTES + (SESSION_EXPIRES_ALERT_MINUTES / 2);

  const state = {
    sessionDate: session?.date,
    sessionExpiredAfter,
    durationMinutes: 0,
    remainingMinutes: 0,
    currentMomentDay: 0,
    sessionMomentDay: 0,
    expired: false,
    isExpiring: false,
    isExpiringBecause: ''
  };

  if (!session) {
    return { ...state, expired: true };
  }

  const currentMomentDate = moment(new Date());
  state.currentMomentDay = currentMomentDate.date();
  const sessionMomentDate = session?.date ? moment(session.date) : 0;
  state.sessionMomentDay = sessionMomentDate ? sessionMomentDate.date() : 0;
  const duration = moment.duration(currentMomentDate.diff(sessionMomentDate));
  state.durationMinutes = duration.asMinutes();
  state.remainingMinutes = state.sessionExpiredAfter - state.durationMinutes;
  state.expired = state.durationMinutes > state.sessionExpiredAfter || state.currentMomentDay !== state.sessionMomentDay;

  if (state.expired) {
    return state;
  }

  if (session.sessionExpiresAtAmericaSaoPauloDate) {
    const expiresAtMomentDate = moment(session.sessionExpiresAtAmericaSaoPauloDate);
    const expiresAtDuration = moment.duration(expiresAtMomentDate.diff(currentMomentDate));
    const expiresAtDurationInMinutes = expiresAtDuration.asMinutes();
    state.isExpiring = expiresAtDurationInMinutes < SESSION_EXPIRES_ALERT_MINUTES;
    state.isExpiringBecause = `${expiresAtDurationInMinutes} < ${SESSION_EXPIRES_ALERT_MINUTES}`;
  } else {
    const mdate = session?.date ? moment(session.date) : 0;
    const expiresAtDuration = moment.duration(currentMomentDate.diff(mdate));
    const expiresAtDurationInMinutes = expiresAtDuration.asMinutes();
    state.isExpiring = expiresAtDurationInMinutes > SESSION_EXPIRES_ALERT_MINUTES;
    state.isExpiringBecause = `${expiresAtDurationInMinutes} > ${SESSION_EXPIRES_ALERT_MINUTES}`;
  }

  return state;
}

export const SessionHelper = {

  getStateExpiration: (session?: Session | null) => {
    return getStateExpiration(session);
  },

  isExpired: (session?: Session | null) => {
    const { expired, sessionExpiredAfter, currentMomentDay, sessionMomentDay, durationMinutes } = getStateExpiration(session);
    console.log(`session expires if ${sessionExpiredAfter} < ${durationMinutes} or ${currentMomentDay} !== ${sessionMomentDay}`);
    if (expired) {
      console.log('session expired');
      Toaster.info('Sua sessão expirou. Para a sua segurança, realize o login novamente.');
    }
    return expired;
  },

  canViewTransport: (session?: Session | null) => {
    if (!session || !session.user) return false;

    const userProfile = session.user.profileType || ProfileType.Viewer;

    if (userProfile == undefined || userProfile == null) return false;

    return ProfileHelper.profilesThatCanViewTransport.some(profile => profile != undefined && profile != null && +profile === +userProfile)
  },

  canManageTransport: (session?: Session | null) => {
    if (!session || !session.user) return false;

    const userProfile = session.user.profileType || ProfileType.Viewer;

    if (userProfile == undefined || userProfile == null) return false;

    return ProfileHelper.profilesThatCanManageTransport.some(profile => profile != undefined && profile != null && +profile === +userProfile)
  },

  canManageStateTransport: (session?: Session | null) => {
    if (!session || !session.user) return false;

    const userProfile = session.user.profileType || ProfileType.Viewer;

    if (userProfile == undefined || userProfile == null) return false;

    return ProfileHelper.profilesThatCanManageStateTransport.some(profile => profile != undefined && profile != null && +profile === +userProfile)
  },

  canSeeTransport: (session?: Session | null) => {
    if (!session || !session.user) return false;

    const userProfile = session.user.profileType || ProfileType.Viewer;

    if (userProfile == undefined || userProfile == null) return false;

    return ProfileHelper.profilesThatCanSeeTransport.some(profile => profile != undefined && profile != null && +profile === +userProfile)
  },

  canManageMultifocalRecipe: (session?: Session | null) => {
    if (!session || !session.user) return false;

    const userProfile = session.user.profileType || ProfileType.Viewer;

    if (userProfile == undefined || userProfile == null) return true;

    return ProfileHelper.profilesThatCanManageMultifocalRecipe.some(profile => profile != undefined && profile != null && +profile === +userProfile);
  },

  canFilterLocation: (session?: Session | null) => {
    if (!session || !session.user) return false;

    const userProfile = session.user.profileType;

    if (userProfile == undefined || userProfile == null) return true;

    return ProfileHelper.profilesThatCanFilterLocation.some(profile => profile != undefined && profile != null && +profile === +userProfile);
  },

  canSeeSimpleTable: (session?: Session | null) => {
    if (!session || !session.user) return false;

    const userProfile = session.user.profileType;

    if (userProfile == undefined || userProfile == null) return false;

    return ProfileHelper.profilesThatCanSeeSimpleTable.some(profile => profile != undefined && profile != null && +profile === +userProfile);
  },

  canFilterSeller: (session?: Session | null) => {
    if (!session || !session.user) return false;

    const userProfile = session.user.profileType;

    if (userProfile == undefined || userProfile == null) return true;

    return ProfileHelper.profilesThatCanFilterSeller.some(profile => profile != undefined && profile != null && +profile === +userProfile);
  },

  canFilterStates: (session?: Session | null) => {
    if (!session || !session.user) return false;

    const userProfile = session.user.profileType;

    if (userProfile == undefined || userProfile == null) return true;

    return ProfileHelper.profilesThatCanFilterStates.some(profile => profile != undefined && profile != null && +profile === +userProfile);
  },

  canFilterPrescriptionStates: (session?: Session | null) => {
    if (!session || !session.user) return false;

    const userProfile = session.user.profileType;

    if (userProfile == undefined || userProfile == null) return true;

    return ProfileHelper.profilesThatCanFilterPrescriptionStates.some(profile => profile != undefined && profile != null && +profile === +userProfile);
  },

  canPrintLabels: (session?: Session | null) => {
    if (!session || !session.user) return false;

    const userProfile = session.user.profileType;

    if (!userProfile == undefined || userProfile == null) return true;

    return ProfileHelper.profilesThatCanPrintLabels.some(profile => profile != undefined && profile != null && +profile === +userProfile);
  },

  canSeeClientDeadlineColumns: (session?: Session | null) => {
    if (!session || !session.user) return false;

    const userProfile = session.user.profileType;

    if (userProfile == undefined || userProfile == null) return false;

    return ProfileHelper.profilesThatCanAlwaysSeeClientDeadlineColumns.some(profile => profile != undefined && profile != null && +profile === +userProfile) ||
      ProfileHelper.profilesThatCanOrCannotSeeClientDeadlineColumns.some(profile => profile != undefined && profile != null && +profile === +userProfile)
      && session.locations?.some(l => l.manageOrderInSislog);
  },

  canSeeInternalDeadlineColumns: (session?: Session | null) => {
    if (!session || !session.user) return false;

    const userProfile = session.user.profileType;

    if (userProfile == undefined || userProfile == null) return false;

    return ProfileHelper.profilesThatCanAlwaysSeeInternalDeadlineColumns.some(profile => profile != undefined && profile != null && +profile === +userProfile) ||
      ProfileHelper.profilesThatCanOrCannotSeeInternalDeadlineColumns.some(profile => profile != undefined && profile != null && +profile === +userProfile)
      && session.locations?.some(l => l.manageOrderInSislog);
  },

  isGlobalStock: (session?: Session | null) => {
    if (!session || !session.user || !session.locations || session.locations.length === 0) return false;

    return session.locations.some(l => l.isGlobalStock);
  },

  canSeeFulfilledByColumn: (session?: Session | null) => {
    if (!session || !session.user) return false;

    const userProfile = session.user.profileType;

    if (userProfile == undefined || userProfile == null) return false;

    return ProfileHelper.profilesThatCanSeeFulfilledByColumn.some(profile => profile != undefined && profile != null && +profile === +userProfile);
  },

  canSeeOrderDetails: (session?: Session | null) => {
    if (!session || !session.user) return false;

    const userProfile = session.user.profileType;

    if (userProfile === null || userProfile === undefined) return false;

    return ProfileHelper.profilesThatCanSeeOrderDetails.some(profile => profile >= 0 && +profile === +userProfile);
  },

  canFilterSpecialTagAndAlert: (session?: Session | null) => {
    if (!session || !session.user) return false;

    const userProfile = session.user.profileType;

    if (userProfile == undefined || userProfile == null) return true;

    return ProfileHelper.profilesThatCanFilterSpecialTagAndAlert.some(profile => profile != undefined && profile != null && +profile === +userProfile);
  },

  canSeeCustomerPhone: (session?: Session | null) => {
    if (!session || !session.user) return false;

    const userProfile = session.user.profileType;

    if (userProfile == undefined || userProfile == null) return false;

    return ProfileHelper.profilesThatCanSeeCustomerPhone.some(profile => profile != undefined && profile != null && +profile === +userProfile);
  },

  canPrintLensSeparationList: (session?: Session | null) => {
    if (!session || !session.user) return false;

    const userProfile = session.user.profileType;

    if (userProfile == undefined || userProfile == null) return false;

    return ProfileHelper.profilesThatCanPrintLensSeparationList.some(profile => profile != undefined && profile != null && +profile === +userProfile);
  },

  canManageProductGroups: (session?: Session | null) => {
    if (!session || !session.user) return false;

    const userProfile = session.user.profileType;

    if (userProfile == undefined || userProfile == null) return false;

    return ProfileHelper.profilesThatCanManageProductGroups.some(profile => profile != undefined && profile != null && +profile === +userProfile);
  },

  canEvolvePriscriptionStateInBatch: (session?: Session | null) => {
    if (!session || !session.user) return false;

    const userProfile = session.user.profileType;

    if (userProfile == undefined || userProfile == null) return false;

    return ProfileHelper.profilesThatCanEvolvePriscriptionStateInBatch.some(profile => profile != undefined && profile != null && +profile === +userProfile);
  },

  canEvolveOrderStateInBatch: (session?: Session | null) => {
    if (!session || !session.user) return false;

    const userProfile = session.user.profileType;

    if (userProfile == undefined || userProfile == null) return false;

    return ProfileHelper.profilesThatCanEvolveOrderStateInBatch.some(profile => profile != undefined && profile != null && +profile === +userProfile);
  },

  canEvolveOrderStates: (session?: Session | null) => {
    if (!session || !session.user) return false;

    const userProfile = session.user.profileType;

    if (userProfile == undefined || userProfile == null) return false;

    return ProfileHelper.profilesThatCanEvolveOrderState.some(profile => profile != undefined && profile != null && +profile === +userProfile);
  },

  canFilterCustomerDeadline: (session?: Session | null) => {
    if (!session || !session.user) return false;

    const userProfile = session.user.profileType;

    if (userProfile == undefined || userProfile == null) return true;

    return ProfileHelper.profilesThatCanFilterCustomerDeadline.some(profile => profile != undefined && profile != null && +profile === +userProfile);
  },

  canFilterDeliveredOrder: (session?: Session | null) => {
    if (!session || !session.user) return false;

    const userProfile = session.user.profileType;

    if (userProfile == undefined || userProfile == null) return true;

    return ProfileHelper.profilesThatCanFilterDeliveredOrder.some(profile => profile != undefined && profile != null && +profile === +userProfile);
  },

  canFilterCustomer: (session?: Session | null) => {
    if (!session || !session.user) return false;

    const userProfile = session.user.profileType;

    if (userProfile == undefined || userProfile == null) return true;

    return ProfileHelper.profilesThatCanFilterCustomer.some(profile => profile != undefined && profile != null && +profile === +userProfile);
  },

  canFilterLensMaterial: (session?: Session | null) => {
    if (!session || !session.user) return false;

    const userProfile = session.user.profileType;

    if (userProfile == undefined || userProfile == null) return true;

    return ProfileHelper.profilesThatCanFilterLensMaterial.some(profile => profile != undefined && profile != null && +profile === +userProfile);
  },

  canManageLensCuttingMachineIntegration: (session?: Session | null) => {
    if (!session || !session.user) return false;

    const userProfile = session.user.profileType;

    if (userProfile == undefined || userProfile == null) return false;

    return ProfileHelper.profilesThatCanManageLensCuttingMachineIntegration.some(profile => profile != undefined && profile != null && +profile === +userProfile);
  },

}